import React, { FunctionComponent, useState } from 'react';
import { Box, ButtonBase, Stack, SxProps, Typography } from '@mui/material';
import moment from 'moment';

import {
  ISection,
  LESSON_TYPES,
  TSectionZoomRecording,
  ZOOM_MEETING_SECTION_PAYMENT_TYPES,
} from '../../../../../../store/V2/SectionSlice';
import { TColumnPermissions } from '..';
import { ILessonHistory } from '../../../../../../store/V2/LessonHistorySlice';
import {
  getFormatedDurationByMinutes,
  getFormatedTableLabels,
  getShortName,
} from '../../../../../../helper';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';
import ViewZoomRecordingsModal from '../../../../../../components/Modals/ViewZoomRecordingsModal';
import { getClassJoinTimeStatusData } from '../../../helper';
import { ICourse } from '../../../../../../store/V2/CoursesSlice';
import { IUser } from '../../../../../../store/UserSlice';

interface CompletedLessonHistoryTableRowProps {
  lessonHistory: ILessonHistory;
  columnPermissions: TColumnPermissions;
  onView: () => void;
  onStudentsClick: () => void;
  onTutorsClick: () => void;
}

const actionButtonStyles: SxProps = {
  borderRadius: '8px',
  padding: '10px 12px 10px 12px',
  fontWeight: 600,
  fontSize: '14px',
  maxHeight: '36px',
  backgroundColor: '#006C9C',
  color: '#F8FAFC',
  width: 'fit-content',
};

const CompletedLessonHistoryTableRow: FunctionComponent<CompletedLessonHistoryTableRowProps> = ({
  lessonHistory,
  columnPermissions,
  onView,
  onStudentsClick,
  onTutorsClick,
}) => {
  // State
  const [
    isViewZoomRecordingsModalOpen,
    setViewZoomRecordingsModalOpen,
  ] = useState(false);

  const paymentTutor = lessonHistory.paymentTutor as IUser | undefined;
  const courseData = lessonHistory.course as ICourse;
  const startMoment = moment(lessonHistory.start);
  const endMoment = lessonHistory.end ? moment(lessonHistory.end) : undefined;
  const sectionData = lessonHistory.section as ISection;
  const referenceKey = lessonHistory.eventId
    ? lessonHistory.eventId.slice(0, 8)
    : '-';

  const lessonType = lessonHistory.lessonType
    ? lessonHistory.lessonType.toLowerCase()
    : '-';

  const paymentType = lessonHistory.paymentType
    ? lessonHistory.paymentType.toLocaleLowerCase().replace('_', ' ')
    : '-';

  let paymentTutorName = '';
  let studentsButtonName = 'No students';
  let zoomRecordingsButtonText = 'No recordings';
  let zoomRecordings: TSectionZoomRecording[] = [];
  let durationText = '-';
  let courseTitle = '-';
  let studentJoinTimeData:
    | { text: string; color: 'info' | 'warning' | 'error' }
    | undefined;

  let tutorJoinTimeData:
    | { text: string; color: 'info' | 'warning' | 'error' }
    | undefined;

  if (lessonHistory.students && lessonHistory.students.length) {
    studentsButtonName = getFormatedTableLabels({
      labels: lessonHistory.students.map((studentItem) =>
        getShortName(studentItem.firstname, studentItem.lastname)
      ),
    });
  }

  if (typeof lessonHistory.duration === 'number') {
    durationText = getFormatedDurationByMinutes({
      minutes: Math.round(lessonHistory.duration),
    });
  }

  if (sectionData) {
    if (sectionData.zoomRecordings && sectionData.zoomRecordings.length) {
      const _length = sectionData.zoomRecordings.length;

      zoomRecordings = sectionData.zoomRecordings;
      zoomRecordingsButtonText = `${_length} Recording${
        _length > 1 ? 's' : ''
      }`;
    }
  }

  if (courseData) {
    courseTitle = courseData.title;
  }

  if (lessonHistory.studentJoinTime) {
    studentJoinTimeData = getClassJoinTimeStatusData(
      startMoment,
      moment(lessonHistory.studentJoinTime)
    );
  } else if (
    startMoment >
      moment(process.env.REACT_APP_CLASS_JOIN_TIME_VALIDATION_DATE).subtract(
        1,
        'month'
      ) &&
    !zoomRecordings.length
  ) {
    studentJoinTimeData = {
      text: 'Missed',
      color: 'error',
    };
  }

  if (lessonHistory.tutorJoinTime) {
    tutorJoinTimeData = getClassJoinTimeStatusData(
      startMoment,
      moment(lessonHistory.tutorJoinTime)
    );
  } else if (
    startMoment >
      moment(process.env.REACT_APP_CLASS_JOIN_TIME_VALIDATION_DATE).subtract(
        1,
        'month'
      ) &&
    !zoomRecordings.length
  ) {
    tutorJoinTimeData = {
      text: 'Missed',
      color: 'error',
    };
  }

  if (paymentTutor) {
    paymentTutorName = getShortName(
      paymentTutor.firstname,
      paymentTutor.lastname
    );
  }

  return (
    <StyledTableRow hover key={lessonHistory._id}>
      {/* <StyledTableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </StyledTableCell> */}
      {columnPermissions.referenceKey ? (
        <StyledTableCell>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {referenceKey}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.dateTime ? (
        <>
          <StyledTableCell>
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              {startMoment.format('ddd MMM DD YYYY')}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#1E293B"
              textTransform="capitalize"
            >
              {startMoment.format('hh:mm A')}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#1E293B"
              textTransform="capitalize"
            >
              {endMoment ? endMoment.format('hh:mm A') : '-'}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#1E293B"
              textTransform="capitalize"
            >
              {durationText}
            </Typography>
          </StyledTableCell>
        </>
      ) : null}
      {columnPermissions.students ? (
        <StyledTableCell>
          <ButtonBase
            sx={{ textDecoration: 'underline', textAlign: 'unset' }}
            onClick={onStudentsClick}
          >
            {studentsButtonName}
          </ButtonBase>
        </StyledTableCell>
      ) : null}
      {columnPermissions.tutors ? (
        <StyledTableCell>
          {paymentTutorName ? (
            <ButtonBase
              sx={{ textDecoration: 'underline', textAlign: 'unset' }}
              onClick={onTutorsClick}
            >
              {paymentTutorName}
            </ButtonBase>
          ) : (
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              -
            </Typography>
          )}
        </StyledTableCell>
      ) : null}
      {columnPermissions.lessonType ? (
        <StyledTableCell>
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            textTransform="capitalize"
          >
            {lessonType}
            {lessonHistory.lessonType === LESSON_TYPES.TRIAL &&
            lessonHistory.isTrialConverted
              ? ` - (Converted)`
              : null}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.paymentType ? (
        <StyledTableCell>
          <Typography
            fontSize="14px"
            fontWeight={500}
            textTransform="capitalize"
            color={
              lessonHistory.paymentType ===
              ZOOM_MEETING_SECTION_PAYMENT_TYPES.BILLABLE
                ? 'success.main'
                : 'warning.main'
            }
          >
            {paymentType}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.course ? (
        <StyledTableCell>
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            className="ellipsis-text two-lines"
            title={courseTitle}
          >
            {courseTitle}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.curriculum ? (
        <StyledTableCell>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {lessonHistory.curriculum}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.subject ? (
        <StyledTableCell>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {lessonHistory.subject}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.grade ? (
        <StyledTableCell>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {lessonHistory.grade}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.studentJoinTime ? (
        <StyledTableCell>
          {studentJoinTimeData ? (
            <Typography fontSize="14px" fontWeight={500}>
              <Stack>
                {lessonHistory.studentJoinTime ? (
                  <>
                    <span>
                      {moment(lessonHistory.studentJoinTime).format(
                        'ddd MMM DD YYYY '
                      )}
                    </span>
                    <span>
                      {moment(lessonHistory.studentJoinTime).format(
                        'hh:mm:ss A'
                      )}
                    </span>
                  </>
                ) : null}
                <Box sx={{ color: `${studentJoinTimeData.color}.main` }}>
                  <span>{studentJoinTimeData.text}</span>
                </Box>
              </Stack>
            </Typography>
          ) : (
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              -
            </Typography>
          )}
        </StyledTableCell>
      ) : null}
      {columnPermissions.tutorJoinTime ? (
        <StyledTableCell>
          {tutorJoinTimeData ? (
            <Typography fontSize="14px" fontWeight={500}>
              <Stack>
                {lessonHistory.tutorJoinTime ? (
                  <>
                    <span>
                      {moment(lessonHistory.tutorJoinTime).format(
                        'ddd MMM DD YYYY'
                      )}
                    </span>
                    <span>
                      {moment(lessonHistory.tutorJoinTime).format('hh:mm:ss A')}
                    </span>
                  </>
                ) : null}
                <Box sx={{ color: `${tutorJoinTimeData.color}.main` }}>
                  <span>{tutorJoinTimeData.text}</span>
                </Box>
              </Stack>
            </Typography>
          ) : (
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              -
            </Typography>
          )}
        </StyledTableCell>
      ) : null}
      {columnPermissions.tutorRate ? (
        <>
          <StyledTableCell>
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              {lessonHistory.tutorRate && lessonHistory.tutorRateCurrency
                ? `${lessonHistory.tutorRateCurrency.toUpperCase()} ${lessonHistory.tutorRate.toFixed(
                    2
                  )}`
                : '-'}
            </Typography>
          </StyledTableCell>
        </>
      ) : null}
      {columnPermissions.tutorAmount ? (
        <StyledTableCell>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {lessonHistory.tutorAmount && lessonHistory.tutorAmountCurrency
              ? `${lessonHistory.tutorAmountCurrency.toUpperCase()} ${lessonHistory.tutorAmount.toFixed(
                  2
                )}`
              : '-'}
          </Typography>
        </StyledTableCell>
      ) : null}
      {columnPermissions.tutorPenalty ? (
        <>
          <StyledTableCell>
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              {lessonHistory.tutorPenalty
                ? `${lessonHistory.tutorPenalty}%`
                : '-'}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              {lessonHistory.tutorPenaltyAmount &&
              lessonHistory.tutorAmountCurrency
                ? `${
                    lessonHistory.tutorAmountCurrency
                  } ${lessonHistory.tutorPenaltyAmount.toFixed(2)}`
                : '-'}
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#1E293B"
              className="ellipsis-text two-lines"
              title={lessonHistory.tutorPenaltyReason}
            >
              {lessonHistory.tutorPenaltyReason || '-'}
            </Typography>
          </StyledTableCell>
        </>
      ) : null}
      {columnPermissions.recordings ? (
        <StyledTableCell>
          <ButtonBase
            disabled={!zoomRecordings.length}
            onClick={() => setViewZoomRecordingsModalOpen(true)}
            sx={{ textDecoration: 'underline', textAlign: 'unset' }}
          >
            {zoomRecordingsButtonText}
          </ButtonBase>
        </StyledTableCell>
      ) : null}
      {columnPermissions.actions ? (
        <StyledTableCell>
          <Stack>
            <ButtonBase sx={{ ...actionButtonStyles }} onClick={onView}>
              View
            </ButtonBase>
          </Stack>
        </StyledTableCell>
      ) : null}
      {isViewZoomRecordingsModalOpen ? (
        <ViewZoomRecordingsModal
          open={isViewZoomRecordingsModalOpen}
          onClose={() => setViewZoomRecordingsModalOpen(false)}
          zoomRecordings={zoomRecordings || []}
        />
      ) : null}
    </StyledTableRow>
  );
};

export default CompletedLessonHistoryTableRow;
